import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, ExternalLink, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { resetSiteContent, saveSiteContent, savedSiteContent, useSiteContent } from '../hooks/useSiteContent';
import { FEATURE_ICONS, SOCIAL_NETWORKS, featureIcon, type SiteContent, type SiteContentKey } from '../lib/siteContent';
import { ImageField, ImageListField } from './SiteImageFields';
import RichTextField from './RichTextField';
import { isRichTextBlank } from '../lib/richText';
import ConfirmationModal from './utils/ConfirmationModal';
import RecipesEditor from './RecipesEditor';
import PostsEditor from './PostsEditor';
import heroImage from '../assets/images/hero-arnie-montagna.webp';
import storiaImage from '../assets/images/arnie_campo.jpg';

interface Props {
  /** executeProtectedAction della dashboard: verifica admin prima di salvare. */
  executeProtectedAction: <T>(action: () => Promise<T>) => Promise<T | null>;
  logUpdateAction: (table: string, id: string, oldValues: Record<string, any>, newValues: Record<string, any>) => unknown;
}

const SECTIONS: { key: SiteContentKey; label: string; group: string; hint: string }[] = [
  { key: 'home.hero', label: 'Apertura', group: 'Home', hint: 'La prima cosa che si vede: foto di sfondo, titolo e pulsante.' },
  { key: 'home.stats', label: 'Numeri', group: 'Home', hint: 'Le piccole cifre sotto i pulsanti di apertura. Da 1 a 4.' },
  { key: 'home.story', label: 'La nostra storia', group: 'Home', hint: 'La sezione a tutta pagina con la foto degli apiari.' },
  { key: 'home.features', label: 'Punti di forza', group: 'Home', hint: 'Le schede con icona sotto la storia. Da 1 a 8.' },
  { key: 'home.cta', label: 'Invito finale', group: 'Home', hint: 'Il riquadro colorato in fondo alla home, prima del footer.' },
  {
    key: 'page.honey',
    label: 'Il nostro miele',
    group: 'Pagine',
    hint: "Pagina /il-miele: lavorazione, conservazione, raccolto e domande frequenti. L'elenco dei mieli arriva dal catalogo.",
  },
  {
    key: 'site.contacts',
    label: 'Contatti e social',
    group: 'Tutto il sito',
    hint: 'Usati in home, footer, pagina contatti e pagine legali. Ragione sociale, P.IVA e indirizzo restano nelle impostazioni di Vercel.',
  },
];

const DEFAULT_IMAGES: Partial<Record<SiteContentKey, string>> = {
  'home.hero': heroImage,
  'home.story': storiaImage,
  'page.honey': storiaImage,
};

/** Dove vedere ogni sezione sul sito. */
const SECTION_URLS: Partial<Record<SiteContentKey, string>> = {
  'page.honey': '/il-miele',
  'site.contacts': '/contact',
};

const MAX_STATS = 4;
const MAX_FEATURES = 8;
const MAX_PAGE_SECTIONS = 12;

const navButtonClass =
  'flex flex-none items-center justify-between gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors';
const navActiveClass = 'bg-card text-foreground shadow-sm ring-1 ring-border/60';
const navIdleClass = 'text-muted-foreground hover:bg-muted';
const MAX_FAQ = 15;
const MAX_SECTION_IMAGES = 4;
const BACKGROUND_HINT =
  'Orizzontale, almeno 1600 px di larghezza, massimo 5 MB. Il testo ci va sopra: meglio una foto senza dettagli importanti a sinistra.';

const inputClass = 'h-10 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground';
const textareaClass = 'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm leading-6 text-foreground';
const iconButtonClass =
  'flex h-10 w-10 md:h-8 md:w-8 flex-none items-center justify-center rounded-lg text-muted-foreground hover:bg-card hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent';

type Drafts = Partial<{ [K in SiteContentKey]: SiteContent[K] }>;

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-xs font-semibold text-muted-foreground">{label}</span>
    {children}
    {hint && <span className="text-xs leading-5 text-muted-foreground">{hint}</span>}
  </label>
);

/** Pulsanti per spostare e togliere una voce di una lista. */
const RowActions: React.FC<{ index: number; count: number; min: number; onMove: (to: number) => void; onRemove: () => void }> = ({
  index,
  count,
  min,
  onMove,
  onRemove,
}) => (
  <div className="flex flex-none gap-0.5">
    <button
      type="button"
      aria-label="Sposta su"
      className={iconButtonClass}
      disabled={index === 0}
      onClick={() => onMove(index - 1)}
    >
      <ArrowUp className="h-4 w-4" />
    </button>
    <button
      type="button"
      aria-label="Sposta giù"
      className={iconButtonClass}
      disabled={index === count - 1}
      onClick={() => onMove(index + 1)}
    >
      <ArrowDown className="h-4 w-4" />
    </button>
    <button type="button" aria-label="Togli" className={iconButtonClass} disabled={count <= min} onClick={onRemove}>
      <Trash2 className="h-4 w-4" />
    </button>
  </div>
);

/**
 * Scheda di una voce ripetuta (punto di forza, paragrafo, domanda): numero,
 * titolo e azioni in una fascia in alto, campi sotto. Il bordo la separa
 * bene dalle altre anche quando sono lunghe.
 */
const ItemCard: React.FC<{
  index: number;
  count: number;
  min: number;
  title: string;
  placeholder: string;
  leading?: React.ReactNode;
  onMove: (to: number) => void;
  onRemove: () => void;
  children: React.ReactNode;
}> = ({ index, count, min, title, placeholder, leading, onMove, onRemove, children }) => (
  <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
    <div className="flex items-center gap-3 border-b border-border bg-muted/60 py-1.5 pl-4 pr-2">
      <span className="flex h-6 min-w-6 flex-none items-center justify-center rounded-full bg-primary/15 px-1.5 text-[11px] font-bold text-primary">
        {index + 1}
      </span>
      {leading}
      <span className={`min-w-0 flex-1 truncate text-sm font-semibold ${title.trim() ? 'text-foreground' : 'italic text-muted-foreground'}`}>
        {title.trim() || placeholder}
      </span>
      <RowActions index={index} count={count} min={min} onMove={onMove} onRemove={onRemove} />
    </div>
    <div className="flex flex-col gap-4 p-4">{children}</div>
  </div>
);

/** Titolo dei gruppi di voci dentro un form ("Paragrafi", "Domande frequenti"). */
const ListHeading: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <div className="mt-3 border-t border-border pt-5">
    <h3 className="font-display text-lg font-semibold text-foreground">{title}</h3>
    {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const move = <T,>(list: T[], from: number, to: number) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

/** Primo campo obbligatorio vuoto o non valido, come messaggio; null se va tutto bene. */
function validate<K extends SiteContentKey>(key: K, draft: SiteContent[K]): string | null {
  const blank = (s: string) => !s.trim();
  switch (key) {
    case 'home.hero': {
      const d = draft as SiteContent['home.hero'];
      if (blank(d.title)) return 'Il titolo non può essere vuoto';
      if (blank(d.text)) return 'Il testo non può essere vuoto';
      if (blank(d.ctaLabel)) return 'Scrivi il testo del pulsante';
      return null;
    }
    case 'home.stats': {
      const d = draft as SiteContent['home.stats'];
      if (d.items.some((i) => blank(i.value) || blank(i.label))) return 'Ogni numero ha bisogno di valore e descrizione';
      return null;
    }
    case 'home.story': {
      const d = draft as SiteContent['home.story'];
      if (blank(d.title) || blank(d.text)) return 'Titolo e testo non possono essere vuoti';
      return null;
    }
    case 'home.features': {
      const d = draft as SiteContent['home.features'];
      if (blank(d.title)) return 'Il titolo della sezione non può essere vuoto';
      if (d.items.some((i) => blank(i.title) || blank(i.description))) return 'Ogni punto di forza ha bisogno di titolo e testo';
      return null;
    }
    case 'home.cta': {
      const d = draft as SiteContent['home.cta'];
      if (blank(d.title) || blank(d.text)) return 'Titolo e testo non possono essere vuoti';
      return null;
    }
    case 'page.honey': {
      const d = draft as SiteContent['page.honey'];
      if (blank(d.title) || blank(d.intro)) return 'Titolo e introduzione non possono essere vuoti';
      if (d.sections.some((x) => blank(x.title) || isRichTextBlank(x.text))) return 'Ogni paragrafo ha bisogno di titolo e testo';
      // I titoli diventano i link "In questa pagina": devono essere diversi.
      if (new Set(d.sections.map((x) => x.title.trim().toLowerCase())).size < d.sections.length)
        return 'Due paragrafi hanno lo stesso titolo';
      if (d.faq.some((x) => blank(x.question) || blank(x.answer))) return 'Ogni domanda ha bisogno della sua risposta';
      if (new Set(d.faq.map((x) => x.question.trim().toLowerCase())).size < d.faq.length) return 'Due domande sono uguali';
      return null;
    }
    case 'site.contacts': {
      const d = draft as SiteContent['site.contacts'];
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())) return "Inserisci un'email valida";
      if (d.phone.trim() && !/^\+?[\d\s./-]{6,}$/.test(d.phone.trim())) return 'Il telefono contiene caratteri non validi';
      for (const name of SOCIAL_NETWORKS) {
        const url = d.social[name].trim();
        if (url && !/^https:\/\/\S+$/.test(url)) return `${name}: il link deve iniziare con https://`;
      }
      return null;
    }
  }
  return null;
}

/** Spazi in testa e in coda tolti da ogni testo prima di salvare. */
const trimDeep = <T,>(value: T): T => {
  if (typeof value === 'string') return value.trim() as T;
  if (Array.isArray(value)) return value.map(trimDeep) as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, trimDeep(v)])) as T;
  }
  return value;
};

/** Vista "Contenuti" della dashboard: testi e foto della home, contatti e social. */
const SiteContentEditor: React.FC<Props> = ({ executeProtectedAction, logUpdateAction }) => {
  const [active, setActive] = useState<SiteContentKey>('home.hero');
  // Ricette ed eventi sono righe di tabelle loro, con un editor a parte.
  const [tableView, setTableView] = useState<'recipes' | 'posts' | null>(null);
  const [drafts, setDrafts] = useState<Drafts>({});
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  // Un hook per sezione: l'ordine delle chiamate resta sempre lo stesso.
  const current: { [K in SiteContentKey]: SiteContent[K] } = {
    'home.hero': useSiteContent('home.hero').content,
    'home.stats': useSiteContent('home.stats').content,
    'home.story': useSiteContent('home.story').content,
    'home.features': useSiteContent('home.features').content,
    'home.cta': useSiteContent('home.cta').content,
    'page.honey': useSiteContent('page.honey').content,
    // Modificata da RecipesEditor, qui solo perché la mappa copra ogni chiave.
    'page.recipes': useSiteContent('page.recipes').content,
    // Modificata da PostsEditor, come sopra.
    'page.posts': useSiteContent('page.posts').content,
    'site.contacts': useSiteContent('site.contacts').content,
  };
  const { loaded } = useSiteContent('home.hero');

  const isDirty = (key: SiteContentKey) => !!drafts[key] && JSON.stringify(drafts[key]) !== JSON.stringify(current[key]);
  const anyDirty = SECTIONS.some((s) => isDirty(s.key));

  // Chi chiude la scheda con modifiche non salvate riceve l'avviso del browser.
  useEffect(() => {
    if (!anyDirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anyDirty]);

  const section = SECTIONS.find((s) => s.key === active)!;
  const draft = (drafts[active] ?? current[active]) as SiteContent[typeof active];
  const isSaved = savedSiteContent(active) !== null;

  function update<K extends SiteContentKey>(key: K, changes: Partial<SiteContent[K]>) {
    setDrafts((prev) => ({ ...prev, [key]: { ...((prev[key] ?? current[key]) as SiteContent[K]), ...changes } }));
  }

  const discard = () =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[active];
      return next;
    });

  const save = async () => {
    const value = trimDeep(draft);
    const problem = validate(active, value);
    if (problem) {
      toast.error(problem);
      return;
    }
    setSaving(true);
    await executeProtectedAction(async () => {
      try {
        const before = savedSiteContent(active);
        await saveSiteContent(active, value);
        await logUpdateAction('site_content', active, { content: before }, { content: value });
        discard();
        toast.success(`${section.label}: salvato. Il sito si aggiorna subito, i motori di ricerca entro pochi minuti.`);
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setSaving(false);
  };

  const reset = async () => {
    setConfirmReset(false);
    setSaving(true);
    await executeProtectedAction(async () => {
      try {
        const before = savedSiteContent(active);
        await resetSiteContent(active);
        await logUpdateAction('site_content', active, { content: before }, { content: null });
        discard();
        toast.success(`${section.label}: tornati i testi originali`);
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setSaving(false);
  };

  const renderForm = () => {
    switch (active) {
      case 'home.hero': {
        const d = draft as SiteContent['home.hero'];
        return (
          <>
            <ImageField
              label="Foto di sfondo"
              value={d.image}
              fallback={heroImage}
              prefix="home-hero"
              hint={BACKGROUND_HINT}
              onChange={(image) => update(active, { image })}
            />
            <Field label="Sopratitolo" hint="La riga piccola sopra il titolo. Puoi lasciarla vuota.">
              <input className={inputClass} value={d.eyebrow} onChange={(e) => update(active, { eyebrow: e.target.value })} />
            </Field>
            <Field label="Titolo">
              <input className={inputClass} value={d.title} onChange={(e) => update(active, { title: e.target.value })} />
            </Field>
            <Field label="Testo" hint="Due o tre frasi. Vai a capo con Invio per un nuovo paragrafo.">
              <textarea
                rows={4}
                className={textareaClass}
                value={d.text}
                onChange={(e) => update(active, { text: e.target.value })}
              />
            </Field>
            <Field label="Testo del pulsante" hint="Il pulsante porta sempre al catalogo prodotti.">
              <input className={inputClass} value={d.ctaLabel} onChange={(e) => update(active, { ctaLabel: e.target.value })} />
            </Field>
            {/* Anteprima con i colori della home */}
            <div className="relative overflow-hidden rounded-xl bg-brand">
              <img src={d.image ?? heroImage} alt="" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-brand/95 via-brand/85 to-brand/60" />
              <div className="relative p-6">
                <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Anteprima</div>
                {d.eyebrow && <p className="mt-3 text-xs font-bold uppercase tracking-widest text-brand-accent">{d.eyebrow}</p>}
                <p className="mt-2 font-display text-2xl font-bold text-white">{d.title}</p>
                <p className="mt-2 max-w-md whitespace-pre-line text-sm leading-6 text-white/90">{d.text}</p>
                <span className="mt-4 inline-block rounded-full bg-action px-4 py-2 text-xs font-bold text-[#2F1500]">
                  {d.ctaLabel}
                </span>
              </div>
            </div>
          </>
        );
      }
      case 'home.stats': {
        const d = draft as SiteContent['home.stats'];
        const setItems = (items: typeof d.items) => update(active, { items });
        return (
          <>
            {d.items.map((item, i) => (
              <div key={i} className="flex flex-wrap items-end gap-3 border-b border-border pb-4 last:border-b-0">
                <div className="w-28">
                  <Field label="Valore">
                    <input
                      className={inputClass}
                      value={item.value}
                      placeholder="100%"
                      onChange={(e) => setItems(d.items.map((it, j) => (j === i ? { ...it, value: e.target.value } : it)))}
                    />
                  </Field>
                </div>
                <div className="min-w-[160px] flex-1">
                  <Field label="Descrizione">
                    <input
                      className={inputClass}
                      value={item.label}
                      placeholder="Puro naturale"
                      onChange={(e) => setItems(d.items.map((it, j) => (j === i ? { ...it, label: e.target.value } : it)))}
                    />
                  </Field>
                </div>
                <RowActions
                  index={i}
                  count={d.items.length}
                  min={1}
                  onMove={(to) => setItems(move(d.items, i, to))}
                  onRemove={() => setItems(d.items.filter((_, j) => j !== i))}
                />
              </div>
            ))}
            {d.items.length < MAX_STATS && (
              <button
                type="button"
                onClick={() => setItems([...d.items, { value: '', label: '' }])}
                className="flex h-10 items-center gap-1.5 self-start rounded-lg bg-muted px-3 text-[13px] font-semibold text-foreground hover:bg-muted/70"
              >
                <Plus className="h-4 w-4" />
                Aggiungi un numero
              </button>
            )}
          </>
        );
      }
      case 'home.story': {
        const d = draft as SiteContent['home.story'];
        return (
          <>
            <ImageField
              label="Foto di sfondo"
              value={d.image}
              fallback={storiaImage}
              prefix={active}
              hint={BACKGROUND_HINT}
              onChange={(image) => update(active, { image })}
            />
            <Field label="Titolo">
              <input className={inputClass} value={d.title} onChange={(e) => update(active, { title: e.target.value })} />
            </Field>
            <Field label="Testo" hint="Vai a capo con Invio per un nuovo paragrafo.">
              <textarea
                rows={6}
                className={textareaClass}
                value={d.text}
                onChange={(e) => update(active, { text: e.target.value })}
              />
            </Field>
          </>
        );
      }
      case 'home.features': {
        const d = draft as SiteContent['home.features'];
        const setItems = (items: typeof d.items) => update(active, { items });
        return (
          <>
            <Field label="Titolo della sezione">
              <input className={inputClass} value={d.title} onChange={(e) => update(active, { title: e.target.value })} />
            </Field>
            <Field label="Introduzione">
              <textarea
                rows={2}
                className={textareaClass}
                value={d.intro}
                onChange={(e) => update(active, { intro: e.target.value })}
              />
            </Field>
            <ListHeading title="Schede" hint="Da 1 a 8. Con le frecce scegli l'ordine in cui compaiono." />
            {d.items.map((item, i) => {
              const Icon = featureIcon(item.icon);
              const setItem = (changes: Partial<typeof item>) =>
                setItems(d.items.map((it, j) => (j === i ? { ...it, ...changes } : it)));
              return (
                <ItemCard
                  key={i}
                  index={i}
                  count={d.items.length}
                  min={1}
                  title={item.title}
                  placeholder="Nuovo punto di forza"
                  leading={<Icon className="h-4 w-4 flex-none text-primary" aria-hidden="true" />}
                  onMove={(to) => setItems(move(d.items, i, to))}
                  onRemove={() => setItems(d.items.filter((_, j) => j !== i))}
                >
                  <div className="grid gap-4 sm:grid-cols-[180px_minmax(0,1fr)]">
                    <Field label="Icona">
                      <select className={inputClass} value={item.icon} onChange={(e) => setItem({ icon: e.target.value })}>
                        {Object.entries(FEATURE_ICONS).map(([name, { label }]) => (
                          <option key={name} value={name}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Titolo">
                      <input className={inputClass} value={item.title} onChange={(e) => setItem({ title: e.target.value })} />
                    </Field>
                  </div>
                  <Field label="Testo">
                    <textarea
                      rows={2}
                      className={textareaClass}
                      value={item.description}
                      onChange={(e) => setItem({ description: e.target.value })}
                    />
                  </Field>
                </ItemCard>
              );
            })}
            {d.items.length < MAX_FEATURES && (
              <button
                type="button"
                onClick={() => setItems([...d.items, { icon: 'Leaf', title: '', description: '' }])}
                className="flex h-10 items-center gap-1.5 self-start rounded-lg bg-muted px-3 text-[13px] font-semibold text-foreground hover:bg-muted/70"
              >
                <Plus className="h-4 w-4" />
                Aggiungi un punto di forza
              </button>
            )}
          </>
        );
      }
      case 'home.cta': {
        const d = draft as SiteContent['home.cta'];
        return (
          <>
            <Field label="Titolo">
              <input className={inputClass} value={d.title} onChange={(e) => update(active, { title: e.target.value })} />
            </Field>
            <Field label="Testo" hint="I pulsanti Contattaci e Vai al carrello restano sempre.">
              <textarea
                rows={4}
                className={textareaClass}
                value={d.text}
                onChange={(e) => update(active, { text: e.target.value })}
              />
            </Field>
          </>
        );
      }
      case 'page.honey': {
        const d = draft as SiteContent['page.honey'];
        const setSections = (sections: typeof d.sections) => update(active, { sections });
        const setFaq = (faq: typeof d.faq) => update(active, { faq });
        return (
          <>
            <ImageField
              label="Foto di sfondo"
              value={d.image}
              fallback={storiaImage}
              prefix={active}
              hint={BACKGROUND_HINT}
              onChange={(image) => update(active, { image })}
            />
            <Field label="Titolo della pagina">
              <input className={inputClass} value={d.title} onChange={(e) => update(active, { title: e.target.value })} />
            </Field>
            <Field label="Introduzione" hint="Compare sotto il titolo e nei risultati di Google: una o due frasi.">
              <textarea
                rows={3}
                className={textareaClass}
                value={d.intro}
                onChange={(e) => update(active, { intro: e.target.value })}
              />
            </Field>

            <ListHeading title="Paragrafi" hint="Ognuno ha un titolo, il testo e fino a 4 foto." />
            {d.sections.map((item, i) => {
              const setItem = (changes: Partial<typeof item>) =>
                setSections(d.sections.map((it, j) => (j === i ? { ...it, ...changes } : it)));
              return (
                <ItemCard
                  key={i}
                  index={i}
                  count={d.sections.length}
                  min={1}
                  title={item.title}
                  placeholder="Nuovo paragrafo"
                  onMove={(to) => setSections(move(d.sections, i, to))}
                  onRemove={() => setSections(d.sections.filter((_, j) => j !== i))}
                >
                  <Field label="Titolo">
                    <input className={inputClass} value={item.title} onChange={(e) => setItem({ title: e.target.value })} />
                  </Field>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-muted-foreground">Testo</span>
                    <RichTextField
                      label={`Testo di "${item.title || 'paragrafo'}"`}
                      value={item.text}
                      onChange={(text) => setItem({ text })}
                    />
                  </div>
                  <ImageListField
                    label="Foto"
                    value={item.images ?? []}
                    max={MAX_SECTION_IMAGES}
                    prefix="page-honey"
                    onChange={(images) => setItem({ images })}
                  />
                </ItemCard>
              );
            })}
            {d.sections.length < MAX_PAGE_SECTIONS && (
              <button
                type="button"
                onClick={() => setSections([...d.sections, { title: '', text: '', images: [] }])}
                className="flex h-10 items-center gap-1.5 self-start rounded-lg bg-muted px-3 text-[13px] font-semibold text-foreground hover:bg-muted/70"
              >
                <Plus className="h-4 w-4" />
                Aggiungi un paragrafo
              </button>
            )}

            <ListHeading title="Domande frequenti" hint="Sul sito si aprono al clic. Puoi anche toglierle tutte." />
            {d.faq.map((item, i) => {
              const setItem = (changes: Partial<typeof item>) =>
                setFaq(d.faq.map((it, j) => (j === i ? { ...it, ...changes } : it)));
              return (
                <ItemCard
                  key={i}
                  index={i}
                  count={d.faq.length}
                  min={0}
                  title={item.question}
                  placeholder="Nuova domanda"
                  onMove={(to) => setFaq(move(d.faq, i, to))}
                  onRemove={() => setFaq(d.faq.filter((_, j) => j !== i))}
                >
                  <Field label="Domanda">
                    <input className={inputClass} value={item.question} onChange={(e) => setItem({ question: e.target.value })} />
                  </Field>
                  <Field label="Risposta">
                    <textarea
                      rows={3}
                      className={textareaClass}
                      value={item.answer}
                      onChange={(e) => setItem({ answer: e.target.value })}
                    />
                  </Field>
                </ItemCard>
              );
            })}
            {d.faq.length < MAX_FAQ && (
              <button
                type="button"
                onClick={() => setFaq([...d.faq, { question: '', answer: '' }])}
                className="flex h-10 items-center gap-1.5 self-start rounded-lg bg-muted px-3 text-[13px] font-semibold text-foreground hover:bg-muted/70"
              >
                <Plus className="h-4 w-4" />
                Aggiungi una domanda
              </button>
            )}
          </>
        );
      }
      case 'site.contacts': {
        const d = draft as SiteContent['site.contacts'];
        return (
          <>
            <Field label="Email" hint="Mostrata nel footer, nella pagina contatti e nelle pagine legali.">
              <input
                type="email"
                className={inputClass}
                value={d.email}
                onChange={(e) => update(active, { email: e.target.value })}
              />
            </Field>
            <Field label="Telefono" hint="Lascialo vuoto per non mostrarlo.">
              <input
                type="tel"
                className={inputClass}
                value={d.phone}
                placeholder="+39 333 123 4567"
                onChange={(e) => update(active, { phone: e.target.value })}
              />
            </Field>
            {SOCIAL_NETWORKS.map((name) => (
              <Field key={name} label={name} hint="Link completo al profilo. Vuoto = pulsante nascosto.">
                <input
                  type="url"
                  className={inputClass}
                  value={d.social[name]}
                  placeholder={`https://www.${name.toLowerCase()}.com/…`}
                  onChange={(e) => update(active, { social: { ...d.social, [name]: e.target.value } })}
                />
              </Field>
            ))}
          </>
        );
      }
    }
  };

  const groups = [...new Set(SECTIONS.map((s) => s.group))];

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <nav className="flex flex-none gap-1 overflow-x-auto lg:sticky lg:top-24 lg:w-52 lg:flex-col lg:overflow-visible">
        {groups.map((group) => (
          <React.Fragment key={group}>
            <div className="hidden px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground first:pt-0 lg:block">
              {group}
            </div>
            {SECTIONS.filter((s) => s.group === group).map((s) => (
              <button
                key={s.key}
                onClick={() => {
                  setActive(s.key);
                  setTableView(null);
                }}
                aria-current={!tableView && active === s.key ? 'page' : undefined}
                className={`${navButtonClass} ${!tableView && active === s.key ? navActiveClass : navIdleClass}`}
              >
                {s.label}
                {isDirty(s.key) && <span className="h-2 w-2 rounded-full bg-primary" aria-label="Modifiche non salvate" />}
              </button>
            ))}
            {group === 'Pagine' && (
              <>
                <button
                  onClick={() => setTableView('recipes')}
                  aria-current={tableView === 'recipes' ? 'page' : undefined}
                  className={`${navButtonClass} ${tableView === 'recipes' ? navActiveClass : navIdleClass}`}
                >
                  Ricette
                </button>
                <button
                  onClick={() => setTableView('posts')}
                  aria-current={tableView === 'posts' ? 'page' : undefined}
                  className={`${navButtonClass} ${tableView === 'posts' ? navActiveClass : navIdleClass}`}
                >
                  Eventi e novità
                </button>
              </>
            )}
          </React.Fragment>
        ))}
      </nav>

      {tableView ? (
        <div className="min-w-0 flex-1">
          {tableView === 'recipes' ? (
            <RecipesEditor executeProtectedAction={executeProtectedAction} />
          ) : (
            <PostsEditor executeProtectedAction={executeProtectedAction} />
          )}
        </div>
      ) : (
        <section className="min-w-0 flex-1 overflow-hidden rounded-xl bg-card shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-6 py-5">
            <div className="min-w-0">
              <h2 className="font-display text-xl font-semibold text-foreground">{section.label}</h2>
              <p className="mt-1 text-sm leading-[22px] text-muted-foreground">{section.hint}</p>
            </div>
            <a
              href={SECTION_URLS[active] ?? '/'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[13px] font-semibold text-primary hover:underline"
            >
              Vedi sul sito
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>

          {!loaded ? (
            <div className="px-6 py-5 text-sm text-muted-foreground">Caricamento…</div>
          ) : (
            <div className="flex flex-col gap-5 px-6 py-5">{renderForm()}</div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-muted/30 px-6 py-4">
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              disabled={!isSaved || saving}
              title={isSaved ? undefined : 'Questa sezione mostra già i testi originali'}
              className="flex h-10 items-center gap-1.5 rounded-lg px-3 text-[13px] font-semibold text-muted-foreground hover:bg-muted disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4" />
              Ripristina originali
            </button>
            <div className="flex items-center gap-2">
              {isDirty(active) && (
                <button
                  type="button"
                  onClick={discard}
                  disabled={saving}
                  className="h-10 rounded-lg px-4 text-[13px] font-semibold text-muted-foreground hover:bg-muted"
                >
                  Annulla modifiche
                </button>
              )}
              <button
                type="button"
                onClick={save}
                disabled={!isDirty(active) || saving}
                className="h-10 rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {saving ? 'Salvo…' : 'Salva e pubblica'}
              </button>
            </div>
          </div>
        </section>
      )}

      <ConfirmationModal
        isOpen={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={reset}
        title={`Ripristinare "${section.label}"?`}
        message={`Il sito tornerà a mostrare i testi${DEFAULT_IMAGES[active] ? ' e la foto' : ''} di partenza. Quelli salvati andranno persi.`}
        confirmText="Ripristina"
        type="warning"
      />
    </div>
  );
};

export default SiteContentEditor;
